import { useMemo, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

// Data table for the shell's list surfaces (admin sessions, recruiter
// compare), rebuilt on beUI's table language: quiet tokens, muted header
// band, pill toolbar, minimal motion. It carries exactly the features those
// surfaces use — search, one quick-filter pill row with live counts,
// blanks-last stable sort, pagination, row click — and no more; the heavier
// grid machinery (selection, bulk bar, detail panels, column resize) died
// with the old vendored implementation because nothing consumed it.

export type DataTableVariant = 'default' | 'panel'
export type DataTableAlign = 'start' | 'center' | 'end'
export type DataTableSortDirection = 'asc' | 'desc'
export type DataTableValue = string | number | boolean | Date | null | undefined

export interface DataTableSort {
  columnId: string
  direction: DataTableSortDirection
}

export interface DataTableColumn<T> {
  /** Stable key. Doubles as the property read off the row when `value` is absent. */
  id: string
  header: ReactNode
  /** Rendered cell. Falls back to the column's value, printed as text. */
  cell?: (row: T, index: number) => ReactNode
  /** The comparable, searchable value behind a cell that renders markup. */
  value?: (row: T) => DataTableValue
  sortable?: boolean
  /** End-aligns the column and switches it to tabular figures so digits line up. */
  numeric?: boolean
  /** Drop the column below this breakpoint instead of letting the table scroll. */
  hideBelow?: 'sm' | 'md' | 'lg'
  className?: string
}

export interface DataTableQuickFilterOption {
  value: string
  label?: ReactNode
}

export interface DataTableQuickFilter {
  /** Column whose value the pills match against. */
  columnId: string
  /** Accessible name for the pill group, e.g. "Filter by status". */
  label?: string
  /** Fixed pill list; defaults to the distinct values in `data`. */
  options?: DataTableQuickFilterOption[]
}

export interface DataTableProps<T> {
  data: readonly T[]
  columns: DataTableColumn<T>[]
  /** Stable identity per row. */
  rowId: (row: T) => string
  /** Names a row for screen readers. Defaults to the first cell. */
  rowLabel?: (row: T) => string
  variant?: DataTableVariant
  /** Sentence describing the table for screen readers. Never painted. */
  caption?: string
  /** Heading at the start of the toolbar. */
  title?: ReactNode
  /** Controls parked at the end of the toolbar. */
  toolbar?: ReactNode
  searchable?: boolean
  searchPlaceholder?: string
  /** Override the haystack a query runs against. */
  searchText?: (row: T) => string
  /** One-tap value pills at the end of the toolbar, with live counts. */
  quickFilter?: DataTableQuickFilter
  defaultSort?: DataTableSort | null
  sort?: DataTableSort | null
  onSortChange?: (sort: DataTableSort | null) => void
  /** Rows per page. Omit to render every row. */
  pageSize?: number
  loading?: boolean
  skeletonRows?: number
  emptyState?: ReactNode
  onRowClick?: (row: T) => void
  className?: string
}

function isEmpty(value: DataTableValue) {
  return value === null || value === undefined || value === ''
}

function compare(a: DataTableValue, b: DataTableValue) {
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function readValue<T>(column: DataTableColumn<T> | undefined, row: T): DataTableValue {
  if (!column) return undefined;
  if (column.value) return column.value(row);
  return (row as Record<string, unknown>)[column.id] as DataTableValue;
}

function printValue(value: DataTableValue) {
  if (isEmpty(value)) return '';
  // ISO rather than a locale format: a locale mismatch between the server
  // render and the client is a hydration error.
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

const HIDE_BELOW: Record<NonNullable<DataTableColumn<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

export function DataTable<T>({
  data,
  columns,
  rowId,
  rowLabel,
  variant = 'default',
  caption,
  title,
  toolbar,
  searchable = false,
  searchPlaceholder = 'Search',
  searchText,
  quickFilter,
  defaultSort = null,
  sort: sortProp,
  onSortChange,
  pageSize,
  loading = false,
  skeletonRows = 5,
  emptyState,
  onRowClick,
  className,
}: DataTableProps<T>) {
  const reduce = useReducedMotion() ?? false
  const [query, setQuery] = useState('')
  const [quickValue, setQuickValue] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [innerSort, setInnerSort] = useState<DataTableSort | null>(defaultSort)
  const sort = sortProp !== undefined ? sortProp : innerSort

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return Array.from(data)
    return Array.from(data).filter((row) => {
      const haystack = searchText
        ? searchText(row)
        : columns.map((column) => printValue(readValue(column, row))).join(' ')
      return haystack.toLowerCase().includes(needle)
    })
  }, [data, query, columns, searchText])

  const readQuickValue = (row: T) => {
    if (!quickFilter) return ''
    const column = columns.find((candidate) => candidate.id === quickFilter.columnId)
    return printValue(readValue(column, row))
  }

  const quickOptions = useMemo<DataTableQuickFilterOption[]>(() => {
    if (!quickFilter) return []
    if (quickFilter.options) return quickFilter.options
    const seen: string[] = []
    for (const row of filtered) {
      const value = readQuickValue(row)
      if (value && !seen.includes(value)) seen.push(value)
    }
    return seen.map((value) => ({ value }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickFilter, filtered, columns])

  // Live counts come from the searched set, so the pills always add up to the
  // rows a click would actually show.
  const quickCounts = useMemo(() => {
    if (!quickFilter) return {} as Record<string, number>
    const counts: Record<string, number> = {}
    for (const row of filtered) {
      const value = readQuickValue(row)
      counts[value] = (counts[value] ?? 0) + 1
    }
    return counts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickFilter, filtered, columns])

  const narrowed = useMemo(() => {
    if (!quickFilter || quickValue === null) return filtered
    return filtered.filter((row) => readQuickValue(row) === quickValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, quickFilter, quickValue, columns])

  const sorted = useMemo(() => {
    if (!sort) return narrowed
    const column = columns.find((candidate) => candidate.id === sort.columnId)
    if (!column) return narrowed
    const direction = sort.direction === 'asc' ? 1 : -1

    // Sort is stable, so equal rows keep their source order. Blanks stay at
    // the bottom in both directions; a column of empties is not a result.
    return [...narrowed].sort((a, b) => {
      const left = readValue(column, a)
      const right = readValue(column, b)
      const leftEmpty = isEmpty(left)
      const rightEmpty = isEmpty(right)
      if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1
      return compare(left, right) * direction
    })
  }, [narrowed, sort, columns])

  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  // Clamped on the way out as well as in the change handlers, so a shrinking
  // result set never renders a blank page for one frame.
  const safePage = Math.min(page, pageCount - 1)
  const visible = useMemo(
    () => (pageSize ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted),
    [sorted, pageSize, safePage],
  )

  const changeQuery = (next: string) => {
    setQuery(next)
    setPage(0)
  }

  const changeQuickValue = (next: string | null) => {
    setQuickValue(next)
    setPage(0)
  }

  const toggleSort = (column: DataTableColumn<T>) => {
    const active = sort?.columnId === column.id
    // Third click clears, so a table can always be put back in source order.
    const next: DataTableSort | null = !active
      ? { columnId: column.id, direction: 'asc' }
      : sort?.direction === 'asc'
        ? { columnId: column.id, direction: 'desc' }
        : null
    if (sortProp === undefined) setInnerSort(next)
    onSortChange?.(next)
  }

  const showToolbar = searchable || quickFilter || title || toolbar
  const hasRows = visible.length > 0

  return (
    <div
      className={cn(
        'w-full overflow-hidden border border-border text-sm',
        variant === 'panel' ? 'rounded-2xl bg-card' : 'bg-background',
        className,
      )}
    >
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          {title ? <span className="mr-2 text-sm font-semibold text-foreground">{title}</span> : null}
          {searchable && (
            <label className="relative block">
              <span className="sr-only">{searchPlaceholder}</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => changeQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 w-60 rounded-full border border-border bg-transparent pl-10 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 hover:border-foreground/30 focus-visible:border-foreground/40 focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            </label>
          )}
          <span className="flex-1" />
          {toolbar}
          {quickFilter && (
            <div role="group" aria-label={quickFilter.label ?? 'Quick filters'} className="flex flex-wrap items-center gap-1.5">
              {quickOptions.map((option) => {
                const active = quickValue === option.value
                const count = quickCounts[option.value] ?? 0
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => changeQuickValue(active ? null : option.value)}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                    )}
                  >
                    {option.label ?? option.value}
                    <span className={cn('tabular-nums', active ? 'text-primary-foreground/80' : 'text-muted-foreground/70')}>
                      {count}
                    </span>
                  </button>
                )
              })}
              {quickValue !== null && (
                <button
                  type="button"
                  onClick={() => changeQuickValue(null)}
                  aria-label="Clear quick filter"
                  className="grid size-8 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table role="grid" className="w-full border-collapse">
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left font-medium text-muted-foreground">
              {columns.map((column) => {
                const active = sort?.columnId === column.id
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      'h-11 px-4 align-middle',
                      column.numeric && 'text-right tabular-nums',
                      column.hideBelow && HIDE_BELOW[column.hideBelow],
                    )}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleSort(column)}
                        className={cn(
                          'inline-flex h-full items-center gap-1 select-none transition-colors hover:text-foreground',
                          active && 'text-foreground',
                          column.numeric && 'flex-row-reverse',
                        )}
                      >
                        <span className="truncate">{column.header}</span>
                        <motion.span
                          aria-hidden="true"
                          className="inline-flex shrink-0"
                          animate={{
                            rotate: active && sort?.direction === 'desc' ? 180 : 0,
                            opacity: active ? 1 : 0.35,
                          }}
                          transition={reduce ? { duration: 0 } : { duration: 0.18 }}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </motion.span>
                      </button>
                    ) : (
                      <span className="block truncate">{column.header}</span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: skeletonRows }, (_, r) => (
                <tr key={r} className="border-border/60 border-b">
                  {columns.map((column) => (
                    <td key={column.id} className={cn('px-4 py-3', column.numeric && 'text-right', column.hideBelow && HIDE_BELOW[column.hideBelow])}>
                      <div className={cn('h-3 animate-pulse rounded-full bg-muted', column.numeric ? 'ml-auto w-10' : 'w-2/3')} />
                    </td>
                  ))}
                </tr>
              ))
            ) : !hasRows ? (
              <tr>
                <td colSpan={columns.length} className="p-10 text-center text-muted-foreground">
                  {emptyState ?? 'No data'}
                </td>
              </tr>
            ) : (
              visible.map((row, index) => {
                const id = rowId(row)
                return (
                  <motion.tr
                    key={id}
                    layout={reduce ? false : 'position'}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={reduce ? { duration: 0 } : { duration: 0.18 }}
                    aria-label={rowLabel?.(row)}
                    data-row-id={id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'border-border/60 border-b transition-colors hover:bg-muted/50',
                      onRowClick && 'cursor-pointer',
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={cn(
                          'px-4 py-3 align-middle text-foreground',
                          column.numeric && 'text-right tabular-nums',
                          column.hideBelow && HIDE_BELOW[column.hideBelow],
                          column.className,
                        )}
                      >
                        {column.cell ? column.cell(row, index) : printValue(readValue(column, row)) || '—'}
                      </td>
                    ))}
                  </motion.tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize && !loading && (
        <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            {sorted.length === 0 ? '0 results' : `${safePage * pageSize + 1}–${Math.min((safePage + 1) * pageSize, sorted.length)} of ${sorted.length}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
              className="grid size-8 place-items-center rounded-full transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-16 text-center tabular-nums">Page {safePage + 1} of {pageCount}</span>
            <button
              type="button"
              aria-label="Next page"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
              className="grid size-8 place-items-center rounded-full transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
